import type { ReactNode } from 'react';
import {
  DoorOpen,
  KeyRound,
  MapPin,
  ScanLine,
  Share2,
  ShieldCheck,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/**
 * InfoDialog — steg-för-steg-instruktion i en dialog, öppnas från en
 * info-ruta eller info-ikon istället för att ligga fast på sidan.
 *
 * Innehållet (stegen) delas mellan Hem ("Så fungerar appen") och
 * nyckelpanelen ("Så delar du en nyckel") så texterna bor på ett ställe.
 */

export interface InfoStep {
  icon: LucideIcon;
  title: string;
  body: string;
}

/** Hem: hur appen fungerar. */
export const HOW_APP_WORKS_STEPS: InfoStep[] = [
  {
    icon: KeyRound,
    title: 'Dina nycklar visas direkt',
    body: 'Alla enheter du har åtkomst till listas på Hem och under Nycklar.',
  },
  {
    icon: DoorOpen,
    title: 'Välj enhet',
    body: 'Tryck på dörren eller enheten du vill använda — nyckeln öppnas direkt.',
  },
  {
    icon: ScanLine,
    title: 'Lås upp',
    body: 'Visa din QR-kod eller håll upp telefonen mot NFC-läsaren.',
  },
];

/** Nyckelpanelen: hur man delar/tilldelar en nyckel. */
export const SHARE_KEY_STEPS: InfoStep[] = [
  {
    icon: DoorOpen,
    title: 'Öppna enheten',
    body: 'Tryck på enheten under Nycklar för att visa dess nyckel.',
  },
  {
    icon: UserPlus,
    title: 'Tilldela nyckel',
    body: 'Tryck på Tilldela nyckel och ange mottagarens namn och mobilnummer — nyckeln skickas som SMS.',
  },
  {
    icon: Share2,
    title: 'Eller dela direkt',
    body: 'Använd Dela på nyckelkortet för en tillfällig QR-kod, eller lägg till nyckeln i din wallet.',
  },
];

/** Nycklar: den valfria funktionen "Nyckel redo när du är framme". */
export const NEARBY_KEY_STEPS: InfoStep[] = [
  {
    icon: KeyRound,
    title: 'Helt valfritt',
    body: 'Dina nycklar fungerar precis som vanligt utan funktionen — du öppnar dem alltid själv under Nycklar.',
  },
  {
    icon: MapPin,
    title: 'Rätt nyckel när du är framme',
    body: 'Med funktionen på visas rätt nyckel automatiskt när du närmar dig dörren och appen är öppen.',
  },
  {
    icon: ShieldCheck,
    title: 'Platsen stannar i telefonen',
    body: 'Positionen jämförs lokalt i appen och lämnar aldrig telefonen. Du kan stänga av när som helst.',
  },
];

interface InfoDialogProps {
  title: string;
  steps: InfoStep[];
  /** Trigger-element (info-ruta eller info-ikon). */
  children: ReactNode;
}

export function InfoDialog({ title, steps, children }: InfoDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          {steps.map(({ icon: Icon, title: stepTitle, body }, i) => (
            <div key={stepTitle} className="flex items-start gap-3.5 rounded-2xl glass-thin p-3.5">
              <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))]">
                <Icon className="h-5 w-5 text-foreground" />
                <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground ring-2 ring-[hsl(var(--background))]">
                  {i + 1}
                </span>
              </div>
              <div className="min-w-0 pt-0.5">
                <p className="font-semibold leading-tight">{stepTitle}</p>
                <p className="mt-1 text-sm leading-snug text-muted-foreground">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
